import isNil from 'lodash/isNil';

import { resolveApiUrl } from '../config';

import { isNotEmptyString } from './checks';
import type { Nullable } from './object';

// Protocols we consider safe to hand to window.open / navigation. Anything else
// (javascript:, data:, vbscript:, file:, ...) can lead to script execution in
// our origin, so it is rejected.
const SAFE_URL_PROTOCOLS: readonly string[] = ['http:', 'https:', 'blob:', 'mailto:'];

// Returns the url unchanged when it resolves to a safe protocol, otherwise null.
// Relative urls resolve against the current origin, so they are always safe.
export const sanitizeUrl = (url: Nullable<string>): Nullable<string> => {
  if (!isNotEmptyString(url)) {
    return null;
  }

  try {
    const { protocol } = new URL(url, window.location.origin);
    return SAFE_URL_PROTOCOLS.includes(protocol) ? url : null;
  } catch {
    return null;
  }
};

// Drop-in replacement for window.open that refuses to open unsafe urls. Returns
// null (and opens nothing) when the url is nil, empty, or uses an unsafe protocol.
export const safeWindowOpen = (
  url: Nullable<string>,
  target?: string,
  features?: string,
): Nullable<Window> => {
  const safeUrl = sanitizeUrl(url);
  if (isNil(safeUrl)) {
    return null;
  }

  return window.open(safeUrl, target, features);
};

// Our Keycloak bearer token is meant for NFS alone - externally hosted files (Saldeo's KSeF
// previews, Google Drive uploads) must never receive it.
export const mayReceiveAuthHeader = (url: Nullable<string>): boolean => {
  if (!isNotEmptyString(url)) {
    return false;
  }

  try {
    const ownOrigin = window.location.origin;
    // The api does not always share our origin - locally it runs on its own port.
    const apiOrigin = new URL(resolveApiUrl(), ownOrigin).origin;

    return [ownOrigin, apiOrigin].includes(new URL(url, ownOrigin).origin);
  } catch {
    return false;
  }
};
